import {
  Box,
  Flex,
  Heading,
  chakra,
  Icon,
  Text,
  Grid,
  GridItem,
  Collapse,
  useToken,
  Spacer,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { getLocation, joinWithCommaAnd, makePluralRegex } from "@/utils/string";
import ProfilePic from "./profile/ProfilePic";
import CollegeImageList from "./profile/CollegeImageList";
import { IoCloseOutline } from "react-icons/io5";
import { AnimatePresence, motion } from "framer-motion";
import { LinkButton, NullableProperty } from "./shared/generic";
import { ChevronRightIcon } from "@chakra-ui/icons";
import Link from "next/link";
import { useUserData } from "@/contexts/UserDataContext";
import { find } from "@/utils/data/colleges-data";
import FavoriteProfilesButton from "./user/FavoriteProfileButton";

const MAX_AMOUNT_COMPARISONS = 5;

const NumericStat = ({ label, count }) => (
  <GridItem>
    <Text fontSize="3xl" fontWeight="bold">
      {count}
    </Text>
    <Text fontSize="md">{makePluralRegex(label, count)}</Text>
  </GridItem>
);

const SimilaritySection = ({
  display_name,
  colleges,
  sat_superscore,
  act_superscore,
  academic_interests,
}) => {
  const { userData } = useUserData();
  const [commonalities, setCommonalities] = useState([]);
  const score = useMemo(() => {
    setCommonalities([]);
    let numerator = 0;
    let denominator = 0;
    const sharedColleges = [];
    const sharedInterests = [];

    for (const targetCollege of userData.targetColleges ?? []) {
      denominator++;
      if (colleges.some(({ name, result }) => name === targetCollege && result === "Accepted")) {
        numerator++;
        sharedColleges.push(targetCollege);
      }
    }
    for (const interest of userData.academicInterests ?? []) {
      denominator++;
      if (academic_interests.includes(interest)) {
        numerator++;
        sharedInterests.push(interest);
      }
    }

    denominator = Math.min(denominator, MAX_AMOUNT_COMPARISONS);
    numerator = Math.min(numerator, denominator);

    if (userData.satScore) {
      denominator++;
      const diff = Math.abs(sat_superscore - userData.satScore);
      if (diff <= 50) {
        numerator++;
        setCommonalities((arr) => [...arr, "📝 Similar SAT score"]);
      }
    }

    if (userData.actScore) {
      denominator++;
      const diff = Math.abs(act_superscore - userData.actScore);
      if (diff <= 2) {
        numerator++;
        setCommonalities((arr) => [...arr, "📝 Similar ACT score"]);
      }
    }

    if (sharedColleges.length > 0) {
      setCommonalities((arr) => [
        ...arr,
        "🏫 Accepted to " +
          joinWithCommaAnd(sharedColleges.map((formalName) => find(formalName)?.name)),
      ]);
    }
    if (sharedInterests.length > 0) {
      setCommonalities((arr) => [...arr, "📚 Studying " + joinWithCommaAnd(sharedInterests, "&")]);
    }
    return numerator / denominator;
  }, [colleges, academic_interests]);

  let bg,
    border,
    phrase,
    button,
    subtitle = "Here are your similarities:";
  if (!userData || !userData.type) {
    phrase = "Want to see how your applications stack up to " + display_name + "'s?";
    bg = "purple.50";
    border = "1px solid purple";
    button = (
      <LinkButton
        mt={2}
        colorScheme="black"
        variant="outline"
        size="sm"
        href="/get-started/hs"
        rightIcon={<ChevronRightIcon />}
      >
        Compare your applications
      </LinkButton>
    );
  } else {
    if (score >= 0.7) {
      phrase = "Your future twin! 👯‍♀️";
      bg = "green.100";
      border = "1px solid green";
    } else if (score >= 0.5) {
      phrase = "Strong match with you!";
      bg = "green.50";
      border = "1px solid green";
    } else if (score >= 0.1) {
      phrase = "Here are your similarities:";
      subtitle = "";
      bg = "gray.50";
      border = "1px solid gray";
    } else {
      return <></>;
    }
  }
  return (
    <Box w={{ base: "100%", md: "inherit" }} flex={1} p={2} borderRadius="md" bg={bg}>
      <Text fontWeight="bold">{phrase}</Text>
      {commonalities.length > 0 && (
        <>
          <Text>{subtitle}</Text>
          {commonalities.map((s, i) => (
            <Text key={i}>{s}</Text>
          ))}
        </>
      )}
      {button}
    </Box>
  );
};

export const ProfileRow = ({
  id,
  index,
  display_name,
  bio,
  colleges,
  sat_superscore,
  act_superscore,
  gpa_w,
  gpa_uw,
  gpa_scale,
  origin_country,
  ethnicity,
  sex,
  academic_interests,
  origin_state,
  university,
  verification,
  n_essays,
  n_awards,
  n_ap_scores,
  n_advice,
  n_ecs,
  n_colleges,
  ...rest
}) => {
  const [expanded, setExpanded] = useState(true);
  const { userData } = useUserData();

  const acceptedColleges = (colleges || []).filter((e) => e.result === "Accepted");
  const colorScheme = index % 2 === 0 ? "purple" : "red";
  const shadowColor = useToken("colors", `${colorScheme}.100`);

  let shortDisplayName = "their";
  if (typeof display_name === "string") {
    const firstName = display_name.split(" ")[0];
    if (!!firstName) {
      shortDisplayName = `${firstName}'s`;
    }
  }

  return (
    <Box
      shadow={`-0.3rem 0.3rem ${shadowColor}`}
      bg="white"
      border="2px solid black"
      borderRadius="lg"
      px={3}
      py={2}
      overflow="visible"
    >
      <Flex
        alignItems="center"
        gap={5}
        w="100%"
        cursor="pointer"
        onClick={() => setExpanded((i) => !i)}
      >
        <ProfilePic uid={id} boxSize={{ base: 10, md: 14 }} borderRadius="full" />
        <Box mx={-4}>
          <FavoriteProfilesButton id={id} />
        </Box>
        <Heading as="h2" flex={1} maxW={{ md: "20rem" }}>
          {/* Stop propagation via a wrapper element so that it doesn't expand */}
          <Box
            onClick={(e) => e.stopPropagation()}
            noOfLines={1}
            sx={{ ":hover": { textDecor: "underline", cursor: "pointer" } }}
          >
            <Link prefetch={false} href={`/profile/${id}`}>
              {display_name}
            </Link>
          </Box>
        </Heading>
        <Spacer />
        <AnimatePresence>
          {!expanded && (
            <CollegeImageList
              display={{ base: "none", md: "flex" }}
              verification={verification}
              key={1}
              enrolled={university}
              as={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              collegeLimit={4}
              w="17rem"
              colleges={acceptedColleges}
            >
              {acceptedColleges.length - 5 > 0 && (
                <chakra.span fontSize="lg" ml={2}>
                  +{acceptedColleges.length - 5} more
                </chakra.span>
              )}
            </CollegeImageList>
          )}
        </AnimatePresence>
        <motion.span animate={{ rotate: !expanded ? "45deg" : "0deg" }}>
          <Icon as={IoCloseOutline} boxSize={{ base: 8, md: 10 }} />
        </motion.span>
      </Flex>

      <Collapse in={expanded}>
        <Text
          fontSize="xl"
          ml={{ md: 26 / 4 + "rem" }}
          mt={{ base: 2, md: 0 }}
          mb={2}
          pb={2}
          borderBottom="1px dashed black"
        >
          {bio}
        </Text>
        <Flex
          ml={{ md: 26 / 4 + "rem" }}
          gap={4}
          flexDir={{ base: "column", md: "row" }}
          align="start"
        >
          {!!userData && (
            <SimilaritySection
              display_name={display_name}
              colleges={colleges}
              sat_superscore={sat_superscore}
              act_superscore={act_superscore}
              academic_interests={academic_interests}
            />
          )}
          <Box flex={1} maxW="20rem">
            <Text>
              <b>Background:</b> {ethnicity.length === 1 ? ethnicity[0] : "Multi-racial"}{" "}
              {!sex.includes(" ") && sex.toLowerCase()} {getLocation(origin_country, origin_state)}
            </Text>
            <NullableProperty label="Academic Interests" value={academic_interests} />
            <NullableProperty label="SAT" nullValue={400} value={sat_superscore} />
            <NullableProperty label="ACT" nullValue={0} value={act_superscore} />
            <NullableProperty label="GPA (W)" nullValue={2} value={gpa_w} />
            <NullableProperty label="GPA (UW)" nullValue={2} value={gpa_uw} />
            <NullableProperty label="GPA Scale" value={gpa_scale} />
          </Box>
          <Box flex={1}>
            <Text fontWeight="bold">College Acceptances</Text>
            <CollegeImageList
              enrolled={university}
              verification={verification}
              colleges={acceptedColleges}
            />
          </Box>

          <Grid
            mt={{ base: 2, md: 0 }}
            flex={1}
            w="100%"
            templateColumns="repeat(3, 1fr)"
            textAlign="center"
          >
            <NumericStat label="essay(/s)" count={n_essays ?? rest.essays?.length} />
            <NumericStat label="college(/s)" count={n_colleges ?? colleges.length} />
            <NumericStat label="award(/s)" count={n_awards ?? rest.awards?.length} />
            <NumericStat label="activit(y/ies)" count={n_ecs ?? rest.ecs?.length} />
            <NumericStat label="Q&A(/s)" count={n_advice ?? rest.advice?.length} />
            <NumericStat label="AP/IB(/s)" count={n_ap_scores ?? rest.ap_scores?.length} />
            <LinkButton
              href={`/profile/${id}`}
              gridColumn="1/4"
              mt={4}
              prefetch={false}
              colorScheme={colorScheme}
              rightIcon={<ChevronRightIcon />}
            >
              <Text style={{ whiteSpace: "normal" }}>Read {shortDisplayName} applications</Text>
            </LinkButton>
          </Grid>
        </Flex>
      </Collapse>

      <Collapse in={!expanded}>
        <CollegeImageList
          display={{ base: "flex", md: "none" }}
          verification={verification}
          enrolled={university}
          w="15rem"
          colleges={acceptedColleges.slice(0, 4)}
        >
          {acceptedColleges.length - 4 > 0 && (
            <chakra.span fontSize="lg" ml={2}>
              +{acceptedColleges.length - 5} more
            </chakra.span>
          )}
        </CollegeImageList>
      </Collapse>
    </Box>
  );
};

export const ProfileTable = ({ profiles }) => {
  return (
    <Flex fontSize="md" direction="column" gap={4}>
      {profiles.map((profile, i) => {
        const id = profile.objectID ?? profile.id;

        if (!profile.colleges) {
          return null;
        }

        return <ProfileRow key={id} index={i} id={id} {...profile} />;
      })}
    </Flex>
  );
};
